import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Anchor, Landmark, Store, Gavel, Scale, SlidersHorizontal, Check, ChevronRight, ShieldCheck, DollarSign } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORIES = [
  { id: "dogane",      labelKey: "reg.cat.dogane",      icon: Anchor,   color: "#0891B2" },
  { id: "finanziaria", labelKey: "reg.cat.finanziaria", icon: Landmark, color: "#7C3AED" },
  { id: "commerciale", labelKey: "reg.cat.commerciale", icon: Store,    color: "#F59E0B" },
  { id: "gare",        labelKey: "reg.cat.gare",        icon: Gavel,    color: "#1F7A47" },
];

const MARKINGS = [
  { id: "CE",   label: "CE",   region: "Europa",       countries: ["Italia","France","Germania","Spagna","Portogallo","Belgio","Paesi Bassi","Austria","Polonia","Repubblica Ceca","Ungheria","Danimarca","Finlandia","Svezia","Grecia","Irlanda","Slovenia","Croazia","Romania","Bulgaria","EU","Europe","Italy","Germany","Spain","Portugal","Belgium","Netherlands","Poland","Denmark","Finland","Sweden","Greece","Ireland","Luxembourg","Norway","Iceland"] },
  { id: "UKCA", label: "UKCA", region: "Regno Unito",   countries: ["Regno Unito","UK","United Kingdom","England","Scotland"] },
  { id: "CSA",  label: "CSA",  region: "Canada",        countries: ["Canada"] },
  { id: "UL",   label: "UL",   region: "USA",           countries: ["Stati Uniti","USA","United States","US"] },
  { id: "ETL",  label: "ETL",  region: "USA",           countries: ["Stati Uniti","USA","United States","US"] },
  { id: "PSE",  label: "PSE",  region: "Giappone",      countries: ["Giappone","Japan"] },
  { id: "RCM",  label: "RCM",  region: "Australia/NZ",  countries: ["Australia","Nuova Zelanda","New Zealand"] },
  { id: "CCC",  label: "CCC",  region: "Cina",          countries: ["Cina","China"] },
  { id: "KC",   label: "KC",   region: "Corea del Sud", countries: ["Corea","Corea del Sud","South Korea","Korea"] },
  { id: "BIS",  label: "BIS",  region: "India",         countries: ["India"] },
  { id: "SASO", label: "SASO", region: "Arabia Saudita", countries: ["Arabia Saudita","Saudi Arabia"] },
  { id: "INMETRO", label: "INMETRO", region: "Brasile", countries: ["Brasile","Brazil"] },
];

function getMarkings(country) {
  if (!country) return [];
  const out = [];
  for (const m of MARKINGS) {
    if (m.countries.some(c => country.toLowerCase().includes(c.toLowerCase()))) out.push(m.id);
  }
  return out;
}

const REGULATIONS = [
  { id: "MiFID II", label: "MiFID II" },
  { id: "Basel III", label: "Basel III" },
  { id: "PSD2", label: "PSD2" },
  { id: "SFDR", label: "SFDR" },
  { id: "DORA", label: "DORA" },
  { id: "GDPR", label: "GDPR" },
  { id: "AML", label: "AML" },
  { id: "FATCA", label: "FATCA" },
  { id: "CRS", label: "CRS" },
  { id: "IFRS", label: "IFRS" },
];

const AMOUNT_BANDS = [
  { id: "s",  label: "< 100k €",       min: 0,        max: 100000 },
  { id: "m",  label: "100k – 1M €",    min: 100000,   max: 1000000 },
  { id: "l",  label: "1M – 10M €",     min: 1000000,  max: 10000000 },
  { id: "xl", label: "> 10M €",        min: 10000000, max: Infinity },
];

function inRegulation(m, ids) {
  const desc = `${m.description || ""} ${m.note || ""} ${m.name || ""}`.toLowerCase();
  for (const id of ids) if (desc.includes(id.toLowerCase())) return true;
  return false;
}

function getRegulations(m) {
  const desc = `${m.description || ""} ${m.note || ""} ${m.name || ""}`.toLowerCase();
  return REGULATIONS.filter(r => desc.includes(r.id.toLowerCase())).map(r => r.id);
}

function scoreColor(s) {
  if (s >= 70) return "#FF3B30";
  if (s >= 40) return "#FF9F0A";
  return "#1F7A47"; // verde pino
}

function statusColor(status) {
  if (status === "aperta") return "#1F7A47"; // verde pino
  if (status === "chiusa") return "#6B7280";
  return "#FF9F0A";
}

export default function CompliancePage() {
  const { t, lang } = useLanguage();
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(new Set());
  const [riskFilter, setRiskFilter] = useState(new Set());
  const [markingFilter, setMarkingFilter] = useState(new Set());
  const [sectorFilter, setSectorFilter] = useState(new Set());
  const [amountFilter, setAmountFilter] = useState(new Set());
  const [regulationFilter, setRegulationFilter] = useState(new Set());
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all(
      CATEGORIES.map(c =>
        axios.get(`${API}/normativa/${c.id}/markers`, { withCredentials: true })
          .then(r => r.data.map((m, i) => ({ ...m, _category: c.id, _id: `${c.id}-${m.id || i}` })))
          .catch(() => [])
      )
    ).then(all => {
      setMarkers(all.flat());
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    let out = markers;
    if (categoryFilter.size > 0) {
      out = out.filter(m => categoryFilter.has(m._category));
    }
    if (riskFilter.size > 0) {
      out = out.filter(m => {
        if (m._category === "gare") return false;
        const s = m.score || 0;
        if (riskFilter.has("high") && s >= 70) return true;
        if (riskFilter.has("mid") && s >= 40 && s < 70) return true;
        if (riskFilter.has("low") && s < 40) return true;
        return false;
      });
    }
    if (markingFilter.size > 0) {
      out = out.filter(m => {
        const marks = getMarkings(m.country || "");
        for (const mk of marks) if (markingFilter.has(mk)) return true;
        return false;
      });
    }
    if (sectorFilter.size > 0) {
      out = out.filter(m => m.sector && sectorFilter.has(m.sector));
    }
    if (amountFilter.size > 0) {
      out = out.filter(m => {
        const amt = m.amount_raw || 0;
        if (!amt) return false;
        for (const bandId of amountFilter) {
          const b = AMOUNT_BANDS.find(x => x.id === bandId);
          if (b && amt >= b.min && amt < b.max) return true;
        }
        return false;
      });
    }
    if (regulationFilter.size > 0) {
      out = out.filter(m => inRegulation(m, Array.from(regulationFilter)));
    }
    return out;
  }, [markers, categoryFilter, riskFilter, markingFilter, sectorFilter, amountFilter, regulationFilter]);

  const availableSectors = useMemo(() => {
    const s = new Set();
    for (const m of markers) if (m._category === "gare" && m.sector) s.add(m.sector);
    return Array.from(s).sort();
  }, [markers]);

  const countsByCategory = useMemo(() => {
    const c = {};
    for (const cat of CATEGORIES) c[cat.id] = markers.filter(m => m._category === cat.id).length;
    return c;
  }, [markers]);

  const activeFilters = categoryFilter.size + riskFilter.size + markingFilter.size + sectorFilter.size + amountFilter.size + regulationFilter.size;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-5 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#00E5FF]/10">
            <Scale className="w-5 h-5 text-[#00E5FF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              {t("reg.title")}
            </h1>
            <p className="text-xs text-[#A0AEC0] mt-0.5 tracking-widest uppercase">
              Panoramica normativa unificata · {markers.length} elementi · {filtered.length} filtrati
            </p>
          </div>
        </div>
      </div>

      {/* Map + Filtri */}
      <div className="flex-1 flex overflow-hidden gap-3 p-3 bg-[#050709]">
        {/* Map */}
        <div className="flex-1 relative rounded-2xl overflow-hidden shadow-xl" style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.4)" }}>
          {!loading && (
            <MapContainer center={[30, 15]} zoom={2} style={{ width: "100%", height: "100%" }} minZoom={2}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap &copy; CARTO'
              />
              {filtered.map(m => {
                const cat = CATEGORIES.find(c => c.id === m._category);
                const mColor = m._category === "gare" ? statusColor(m.status) : scoreColor(m.score);
                const isHover = hoveredId === m._id;
                const isFiltered = activeFilters > 0;
                const isActive = isHover || isFiltered;
                return (
                  <CircleMarker
                    key={m._id}
                    center={[m.lat, m.lng]}
                    radius={isHover ? 6 : 4}
                    pathOptions={{
                      fillColor: isActive ? mColor : "#FFFFFF",
                      fillOpacity: isActive ? 0.9 : 0.85,
                      color: isActive ? (cat?.color || mColor) : "#94A3B8",
                      weight: isHover ? 2 : 1,
                    }}
                    eventHandlers={{
                      mouseover: () => setHoveredId(m._id),
                      mouseout: () => setHoveredId(prev => (prev === m._id ? null : prev)),
                    }}
                  >
                    <Popup
                      className="compliance-popup"
                      closeButton={false}
                      autoPan={true}
                      offset={[0, -2]}
                    >
                      <div className="min-w-[220px] max-w-[260px]">
                        <div className="flex items-center gap-2 mb-2">
                          {cat && (
                            <div className="w-7 h-7 flex items-center justify-center rounded-md flex-shrink-0" style={{ backgroundColor: `${cat.color}22` }}>
                              <cat.icon className="w-3.5 h-3.5" style={{ color: cat.color }} />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] font-bold text-white leading-tight truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
                              {m.country || m.name}
                            </p>
                            <p className="text-[9px] tracking-[0.15em] uppercase mt-0.5" style={{ color: cat?.color }}>{cat && t(cat.labelKey)}</p>
                          </div>
                        </div>

                        {m.score !== undefined && (
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[9px] tracking-widest uppercase text-[#6B7280] font-semibold">Score</span>
                            <span className="text-lg font-bold tabular-nums" style={{ color: mColor, fontFamily: "Chivo, sans-serif" }}>{m.score}</span>
                            <span className="text-[9px] text-[#6B7280]">/100</span>
                            <span
                              className="ml-auto text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider"
                              style={{ color: mColor, backgroundColor: `${mColor}18`, border: `1px solid ${mColor}55` }}
                            >
                              {m.score >= 70 ? "Alto" : m.score >= 40 ? "Medio" : "Basso"}
                            </span>
                          </div>
                        )}

                        {m._category === "gare" && (
                          <div className="space-y-1 mb-2">
                            {m.sector && <p className="text-[11px] text-[#CBD5E1]"><span className="text-[#6B7280]">Settore:</span> <span className="text-white font-medium">{m.sector}</span></p>}
                            {m.amount && <p className="text-[11px] text-[#F59E0B]"><span className="text-[#6B7280]">Importo:</span> {m.amount}</p>}
                            {m.deadline && <p className="text-[11px] text-[#FF9F0A]"><span className="text-[#6B7280]">Scadenza:</span> {m.deadline}</p>}
                          </div>
                        )}

                        <button
                          data-testid="compliance-popup-open-detail"
                          onClick={(e) => { e.stopPropagation(); setSelected(m); }}
                          className="w-full mt-1 text-[10px] tracking-[0.15em] uppercase font-semibold px-3 py-2 rounded-md transition-colors flex items-center justify-center gap-1.5"
                          style={{ backgroundColor: `${cat?.color || "#00E5FF"}18`, color: cat?.color || "#00E5FF", border: `1px solid ${cat?.color || "#00E5FF"}55` }}
                        >
                          {t("reg.popup.open_detail")}
                        </button>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          )}
        </div>

        {/* Sidebar filtri + lista */}
        <div className="w-80 flex flex-col gap-3 overflow-hidden flex-shrink-0">
          {/* Panoramica */}
          <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }}>
            <div className="px-4 py-2.5 flex items-center gap-2">
              <Scale className="w-3.5 h-3.5 text-[#00E5FF]" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#00E5FF] font-bold">Panoramica</p>
              <span className="ml-auto text-[10px] text-[#6B7280] tabular-nums">{markers.length} totali</span>
            </div>
            <div className="px-3 pb-3 flex flex-col">
              {CATEGORIES.map(cat => (
                <div key={cat.id} className="flex items-center gap-3 px-2 py-1.5 hover:bg-[#12161C] transition-colors rounded-md">
                  <cat.icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: cat.color }} />
                  <p className="text-sm font-bold text-white tabular-nums leading-none flex-shrink-0 w-8 text-right" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {countsByCategory[cat.id]}
                  </p>
                  <p className="text-[10px] tracking-[0.14em] uppercase text-[#CBD5E1] truncate">{t(cat.labelKey)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Filtri */}
          <div className="bg-[#0D0F12] border border-white/[0.04]" style={{ borderRadius: "2px" }}>
            <div className="px-4 py-2.5 flex items-center gap-2">
              <SlidersHorizontal className="w-3.5 h-3.5 text-[#00E5FF]" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#00E5FF] font-bold">{t("reg.filters_title")}</p>
              {activeFilters > 0 && (
                <button
                  data-testid="compliance-filters-reset"
                  onClick={() => { setCategoryFilter(new Set()); setRiskFilter(new Set()); setMarkingFilter(new Set()); setSectorFilter(new Set()); setAmountFilter(new Set()); setRegulationFilter(new Set()); }}
                  className="ml-auto text-[9px] tracking-wider uppercase text-[#00E5FF] hover:text-white"
                >Reset</button>
              )}
            </div>

            {/* Categoria dropdown */}
            <Dropdown label={t("reg.filter.category")} summaryText={categoryFilter.size === 0 ? t("common.all_m") : `${categoryFilter.size} ${lang === "en" ? "sel." : "selez."}`}>
              {CATEGORIES.map(cat => {
                const active = categoryFilter.has(cat.id);
                return (
                  <FilterRow
                    key={cat.id}
                    dataTestId={`compliance-cat-${cat.id}`}
                    active={active}
                    color={cat.color}
                    onClick={() => setCategoryFilter(prev => toggleSet(prev, cat.id))}
                  >
                    <cat.icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: cat.color }} />
                    <span className="flex-1 text-left tracking-wider uppercase">{t(cat.labelKey)}</span>
                    <span className="text-[10px] text-[#6B7280] tabular-nums font-mono">{countsByCategory[cat.id]}</span>
                  </FilterRow>
                );
              })}
            </Dropdown>

            {/* Rischio dropdown */}
            <Dropdown label={t("reg.filter.risk")} summaryText={riskFilter.size === 0 ? t("common.all_m") : `${riskFilter.size} ${lang === "en" ? "sel." : "selez."}`}>
              {[
                { k: "high", color: "#FF3B30", label: `${t("reg.risk.high")} (≥70)` },
                { k: "mid",  color: "#FF9F0A", label: `${t("reg.risk.mid")} (40-69)` },
                { k: "low",  color: "#1F7A47", label: `${t("reg.risk.low")} (<40)` },
              ].map(opt => {
                const active = riskFilter.has(opt.k);
                return (
                  <FilterRow
                    key={opt.k}
                    dataTestId={`compliance-risk-${opt.k}`}
                    active={active}
                    color={opt.color}
                    onClick={() => setRiskFilter(prev => toggleSet(prev, opt.k))}
                  >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: opt.color, boxShadow: `0 0 4px ${opt.color}` }} />
                    <span className="flex-1 text-left tracking-wider uppercase">{opt.label}</span>
                  </FilterRow>
                );
              })}
            </Dropdown>

            {/* Marcatura dropdown */}
            <Dropdown label={t("reg.filter.marking")} summaryText={markingFilter.size === 0 ? t("common.all_m") : `${markingFilter.size} ${lang === "en" ? "sel." : "selez."}`}>
              {MARKINGS.map(mk => {
                const active = markingFilter.has(mk.id);
                return (
                  <FilterRow
                    key={mk.id}
                    dataTestId={`compliance-marking-${mk.id}`}
                    active={active}
                    color="#00E5FF"
                    onClick={() => setMarkingFilter(prev => toggleSet(prev, mk.id))}
                  >
                    <ShieldCheck className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0" />
                    <span className="flex-1 text-left">
                      <span className={`tracking-wider font-mono font-bold ${active ? "text-white" : "text-[#CBD5E1]"}`}>{mk.label}</span>
                      <span className="text-[9px] text-[#6B7280] ml-1.5">{mk.region}</span>
                    </span>
                  </FilterRow>
                );
              })}
            </Dropdown>

            {/* Settore Gara dropdown */}
            {availableSectors.length > 0 && (
              <Dropdown label={t("reg.filter.sector")} summaryText={sectorFilter.size === 0 ? t("common.all_m") : `${sectorFilter.size} ${lang === "en" ? "sel." : "selez."}`}>
                {availableSectors.map(s => {
                  const active = sectorFilter.has(s);
                  return (
                    <FilterRow
                      key={s}
                      dataTestId={`compliance-sector-${s}`}
                      active={active}
                      color="#16C784"
                      onClick={() => setSectorFilter(prev => toggleSet(prev, s))}
                    >
                      <Gavel className="w-3.5 h-3.5 text-[#16C784] flex-shrink-0" />
                      <span className="flex-1 text-left tracking-wider">{s}</span>
                    </FilterRow>
                  );
                })}
              </Dropdown>
            )}

            {/* Importo Gara dropdown */}
            <Dropdown label={t("reg.filter.amount")} summaryText={amountFilter.size === 0 ? t("common.all_m") : `${amountFilter.size} ${lang === "en" ? "sel." : "selez."}`}>
              {AMOUNT_BANDS.map(b => {
                const active = amountFilter.has(b.id);
                return (
                  <FilterRow
                    key={b.id}
                    dataTestId={`compliance-amount-${b.id}`}
                    active={active}
                    color="#F59E0B"
                    onClick={() => setAmountFilter(prev => toggleSet(prev, b.id))}
                  >
                    <DollarSign className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0" />
                    <span className="flex-1 text-left tracking-wider">{b.label}</span>
                  </FilterRow>
                );
              })}
            </Dropdown>

            {/* Normativa Finanziaria dropdown */}
            <Dropdown label={t("reg.filter.regulation")} summaryText={regulationFilter.size === 0 ? t("common.all_m") : `${regulationFilter.size} ${lang === "en" ? "sel." : "selez."}`} last>
              {REGULATIONS.map(r => {
                const active = regulationFilter.has(r.id);
                return (
                  <FilterRow
                    key={r.id}
                    dataTestId={`compliance-regulation-${r.id}`}
                    active={active}
                    color="#A78BFA"
                    onClick={() => setRegulationFilter(prev => toggleSet(prev, r.id))}
                  >
                    <Landmark className="w-3.5 h-3.5 text-[#A78BFA] flex-shrink-0" />
                    <span className="flex-1 text-left tracking-wider font-mono">{r.label}</span>
                  </FilterRow>
                );
              })}
            </Dropdown>
          </div>

          {/* Lista */}
          <div className="bg-[#0D0F12] border border-white/[0.06] rounded-xl shadow-lg flex-1 overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 border-b border-white/[0.06] flex-shrink-0">
              <p className="text-[11px] tracking-[0.2em] uppercase text-white font-bold">Elementi <span className="text-[#6B7280] font-normal">· {filtered.length}</span></p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filtered.map(m => {
                const cat = CATEGORIES.find(c => c.id === m._category);
                const mColor = m._category === "gare" ? statusColor(m.status) : scoreColor(m.score);
                return (
                  <div key={m._id} data-testid="compliance-row"
                    onClick={() => setSelected(m)}
                    className="px-4 py-3 border-b border-white/[0.04] hover:bg-[#1A1D24] transition-colors cursor-pointer">
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: mColor }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {cat && <cat.icon className="w-3 h-3 flex-shrink-0" style={{ color: cat.color }} />}
                          <p className="text-xs text-white font-medium truncate">{m.country || m.name}</p>
                        </div>
                        <p className="text-[9px] tracking-wider uppercase mt-0.5" style={{ color: cat?.color }}>{cat && t(cat.labelKey)}</p>
                        {m.score !== undefined && (
                          <p className="text-[10px] text-[#A0AEC0] mt-0.5">Score: <span className="font-bold" style={{ color: mColor }}>{m.score}</span></p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Detail Panel slide-over */}
      {selected && <ComplianceDetailPanel marker={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ─── Detail Panel (slide-over) ─────────────────────────────────────────────
function ComplianceDetailPanel({ marker, onClose }) {
  const { t } = useLanguage();
  const cat = CATEGORIES.find(c => c.id === marker._category);
  const Icon = cat?.icon || Scale;
  const isGare = marker._category === "gare";
  const mColor = isGare ? statusColor(marker.status) : scoreColor(marker.score);
  const availableMarkings = getMarkings(marker.country || "");
  const availableRegs = getRegulations(marker);

  return (
    <div data-testid="compliance-detail-panel" className="fixed inset-0 flex z-[9999] animate-fadeIn" onClick={onClose}>
      <div className="flex-1 bg-black/70 backdrop-blur-sm" />
      <div onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-[720px] bg-[#0A0C10] flex flex-col overflow-hidden shadow-[0_0_60px_rgba(0,229,255,0.15)] animate-slideInRight">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-white/[0.04] bg-[#0D0F12]">
          <div className="w-11 h-11 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: `${cat?.color || "#00E5FF"}18` }}>
            <Icon className="w-5 h-5" style={{ color: cat?.color || "#00E5FF" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>
                {isGare ? marker.name : marker.country}
              </h2>
              {isGare && marker.status && (
                <span
                  className="text-[9px] tracking-widest uppercase px-2 py-0.5 font-bold rounded"
                  style={{ color: "#000", backgroundColor: statusColor(marker.status) }}
                >{marker.status}</span>
              )}
            </div>
            <p className="text-[10px] tracking-widest uppercase mt-1" style={{ color: cat?.color }}>
              {cat && t(cat.labelKey)} {isGare && marker.country ? `· ${marker.country}` : ""}
            </p>
          </div>
          <button
            data-testid="compliance-detail-close"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-white/[0.04] text-[#A0AEC0] hover:text-white hover:bg-white/10 transition-colors"
          >✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Score / Amount */}
          {!isGare ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#0D0F12] border border-white/[0.06] rounded-lg px-4 py-3">
                <p className="text-[9px] tracking-[0.22em] uppercase text-[#6B7280] font-semibold">Rischio</p>
                <p className="text-3xl font-bold tabular-nums mt-1" style={{ color: mColor, fontFamily: "Chivo, sans-serif" }}>{marker.score}<span className="text-lg text-[#6B7280] ml-1">/100</span></p>
                <p className="text-[11px] mt-1 font-semibold uppercase tracking-wider" style={{ color: mColor }}>
                  {marker.score >= 70 ? "Alto" : marker.score >= 40 ? "Medio" : "Basso"}
                </p>
              </div>
              <div className="bg-[#0D0F12] border border-white/[0.06] rounded-lg px-4 py-3">
                <p className="text-[9px] tracking-[0.22em] uppercase text-[#6B7280] font-semibold">Coordinate</p>
                <p className="text-sm text-white font-mono tabular-nums mt-2">{marker.lat?.toFixed(2)}, {marker.lng?.toFixed(2)}</p>
                <p className="text-[10px] text-[#A0AEC0] mt-1">Localizzazione geografica</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#0D0F12] border border-white/[0.06] rounded-lg px-4 py-3">
                <p className="text-[9px] tracking-[0.22em] uppercase text-[#6B7280] font-semibold">Importo</p>
                <p className="text-2xl font-bold tabular-nums mt-1 text-[#F59E0B]" style={{ fontFamily: "Chivo, sans-serif" }}>{marker.amount || "N/D"}</p>
                <p className="text-[10px] text-[#A0AEC0] mt-1">{marker.sector}</p>
              </div>
              <div className="bg-[#0D0F12] border border-white/[0.06] rounded-lg px-4 py-3">
                <p className="text-[9px] tracking-[0.22em] uppercase text-[#6B7280] font-semibold">Scadenza</p>
                <p className="text-lg font-bold tabular-nums mt-1 text-[#FF9F0A]">{marker.deadline || "N/D"}</p>
                <p className="text-[10px] text-[#A0AEC0] mt-1">Data limite presentazione</p>
              </div>
            </div>
          )}

          {/* Description */}
          {marker.description && (
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#6B7280] font-semibold mb-2">Descrizione</p>
              <p className="text-sm text-[#CBD5E1] leading-relaxed">{marker.description}</p>
            </div>
          )}

          {/* Note operative */}
          {marker.note && (
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#6B7280] font-semibold mb-2">Note operative</p>
              <div className="bg-[#0D0F12] border border-white/[0.06] rounded-lg px-4 py-3">
                <p className="text-sm text-[#CBD5E1] leading-relaxed">{marker.note}</p>
              </div>
            </div>
          )}

          {/* Marcature */}
          {availableMarkings.length > 0 && (
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#6B7280] font-semibold mb-2">Marcature applicabili</p>
              <div className="flex flex-wrap gap-2">
                {availableMarkings.map(mk => {
                  const info = MARKINGS.find(x => x.id === mk);
                  return (
                    <div key={mk} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-md">
                      <ShieldCheck className="w-3.5 h-3.5 text-[#00E5FF]" />
                      <span className="text-xs font-mono font-bold text-white">{mk}</span>
                      <span className="text-[9px] text-[#A0AEC0]">{info?.region}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Normative rilevate */}
          {availableRegs.length > 0 && (
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#6B7280] font-semibold mb-2">Normative rilevate</p>
              <div className="flex flex-wrap gap-2">
                {availableRegs.map(r => (
                  <div key={r} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#A78BFA]/10 border border-[#A78BFA]/30 rounded-md">
                    <Landmark className="w-3.5 h-3.5 text-[#A78BFA]" />
                    <span className="text-xs font-mono font-bold text-white">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Link (gare) */}
          {marker.link && (
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#6B7280] font-semibold mb-2">Link procedura</p>
              <a
                href={marker.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] text-xs font-semibold tracking-wider uppercase hover:bg-[#00E5FF]/20 transition-colors rounded"
              >
                Apri procedura
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Helper components ──────────────────────────────────────────────────────
function toggleSet(prev, val) {
  const next = new Set(prev);
  if (next.has(val)) next.delete(val); else next.add(val);
  return next;
}

function Dropdown({ label, summaryText, children, last }) {
  return (
    <details className={`group ${last ? "" : "border-b border-white/[0.06]"}`}>
      <summary className="list-none cursor-pointer px-4 py-2.5 flex items-center gap-2 hover:bg-[#12161C] transition-colors">
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#CBD5E1] font-semibold">{label}</p>
        <span className="ml-auto flex items-center gap-1.5">
          <span className="text-[10px] text-white font-semibold">{summaryText}</span>
          <ChevronRight className="w-3 h-3 text-[#8B9AB0] transition-transform group-open:rotate-90" />
        </span>
      </summary>
      <div className="px-2 pb-3 pt-1 flex flex-col gap-1 max-h-64 overflow-y-auto">
        {children}
      </div>
    </details>
  );
}

function FilterRow({ dataTestId, active, color, onClick, children }) {
  return (
    <button
      data-testid={dataTestId}
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-[11px] rounded-md transition-colors ${active ? "" : "hover:bg-[#12161C]"}`}
      style={active ? {
        backgroundColor: `${color}26`,
        boxShadow: `inset 0 0 0 1px ${color}66`,
      } : undefined}
    >
      <span
        className="w-4 h-4 rounded-[3px] flex items-center justify-center flex-shrink-0 transition-colors"
        style={{
          backgroundColor: active ? color : "transparent",
          border: `1.5px solid ${active ? color : "#3A4152"}`,
        }}
      >
        {active && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
      </span>
      <span className={`flex items-center gap-2 flex-1 ${active ? "text-white font-bold" : "text-[#CBD5E1]"}`}>
        {children}
      </span>
    </button>
  );
}
